<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { Copy, ImagePlus, X } from 'lucide-vue-next'
import { accountsApi, ApiError, customersApi, employeesApi, vehiclesApi, workOrdersApi, type CreateCustomerAccountResponse, type CustomerSummary } from '@ck/api-client'
import { digitsOnly, formatCep, formatCpfCnpj, formatPhone, formatDateInput, normalizeCep, normalizeCpfCnpj, normalizePhone, normalizeDateOnly } from '@ck/domain'
import { CkAlert, CkButton, CkField } from '@ck/ui'
import { compressImage } from '../lib/compressImage'

const emit = defineEmits<{ created: [workOrderId: string] }>()

const queryClient = useQueryClient()
const techniciansQuery = useQuery({ queryKey: ['employees'], queryFn: () => employeesApi.list() })
const technicians = computed(() => (techniciansQuery.data.value ?? []).filter((employee) => employee.role === 'technician' && employee.active))

const customerForm = reactive({ name: '', email: '', phone: '', cpfCnpj: '', cep: '' })
const vehicleForm = reactive({ brand: '', model: '', year: '', plate: '', vin: '', color: '' })
const orderForm = reactive({ assignedTechnicianSubject: '', expectedDelivery: '' })

const searchTerm = ref('')
const activeSearch = ref('')
const searched = ref(false)
const selectedCustomer = ref<CustomerSummary | null>(null)
const customersQuery = useQuery(computed(() => ({
  queryKey: ['customers-search', activeSearch.value],
  queryFn: () => customersApi.list(activeSearch.value),
  enabled: activeSearch.value.trim().length >= 2
})))
const searchResults = computed(() => customersQuery.data.value?.items ?? [])

function runSearch() {
  searched.value = true
  activeSearch.value = searchTerm.value.trim()
}
function selectCustomer(customer: CustomerSummary) {
  selectedCustomer.value = customer
  errors.value.name = undefined
  errors.value.email = undefined
  errors.value.phone = undefined
  errors.value.cpfCnpj = undefined
  errors.value.cep = undefined
}
function clearSelection() {
  selectedCustomer.value = null
  createdAccount.value = null
}

// Foto de perfil do veículo (capa) — opcional. Se escolhida, é enviada logo após a OS ser criada,
// pelo endpoint dedicado; falha aqui não desfaz a OS (a capa pode ser adicionada depois no detalhe).
const coverFile = ref<File | null>(null)
const coverPreview = ref<string | null>(null)
const coverInput = ref<HTMLInputElement | null>(null)
const coverWarning = ref(false)
function triggerCoverPick() { coverInput.value?.click() }
function onCoverSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  if (coverPreview.value) URL.revokeObjectURL(coverPreview.value)
  coverFile.value = file
  coverPreview.value = URL.createObjectURL(file)
}
function clearCover() {
  if (coverPreview.value) URL.revokeObjectURL(coverPreview.value)
  coverFile.value = null
  coverPreview.value = null
}

const createdAccount = ref<CreateCustomerAccountResponse | null>(null)
const createdWorkOrderId = ref<string | null>(null)
const copied = ref(false)
async function copyPassword() {
  if (!createdAccount.value) return
  await navigator.clipboard.writeText(createdAccount.value.temporaryPassword)
  copied.value = true
}

interface FormErrors {
  name?: string; email?: string; phone?: string; cpfCnpj?: string; cep?: string; brand?: string; model?: string; year?: string; plate?: string; expectedDelivery?: string
}
const errors = ref<FormErrors>({})
const submitError = ref<string | null>(null)

const mutation = useMutation({
  mutationFn: async () => {
    createdAccount.value = null
    let customerId: string
    if (selectedCustomer.value) {
      customerId = selectedCustomer.value.id
    } else {
      const account = await accountsApi.createCustomerAccount({
        name: customerForm.name.trim(),
        email: customerForm.email.trim(),
        phone: normalizePhone(customerForm.phone),
        cpfCnpj: normalizeCpfCnpj(customerForm.cpfCnpj),
        cep: normalizeCep(customerForm.cep)
      })
      createdAccount.value = account
      customerId = account.customerId
    }
    const vehicle = await vehiclesApi.create({
      customerId,
      brand: vehicleForm.brand.trim(),
      model: vehicleForm.model.trim(),
      year: Number(vehicleForm.year),
      plate: vehicleForm.plate.trim().toUpperCase(),
      vin: vehicleForm.vin.trim() || null,
      color: vehicleForm.color.trim() || null
    })
    const workOrder = await workOrdersApi.create({
      vehicleId: vehicle.id,
      assignedTechnicianSubject: orderForm.assignedTechnicianSubject || null,
      expectedDelivery: normalizeDateOnly(orderForm.expectedDelivery) ? `${normalizeDateOnly(orderForm.expectedDelivery)}T00:00:00.000Z` : null
    })
    if (coverFile.value) {
      try {
        const compressed = await compressImage(coverFile.value)
        await workOrdersApi.uploadCover(workOrder.id, compressed)
      } catch {
        // OS já existe; sinaliza que a capa não subiu, sem falhar a criação.
        coverWarning.value = true
      }
    }
    return workOrder
  },
  onSuccess: async (workOrder) => {
    await queryClient.invalidateQueries({ queryKey: ['work-orders'] })
    createdWorkOrderId.value = workOrder.id
    if (!createdAccount.value) emit('created', workOrder.id)
  },
  onError: (error: unknown) => {
    submitError.value = error instanceof ApiError ? (error.problem.detail ?? error.problem.title) : 'Não foi possível criar a ordem de serviço.'
  }
})

function validate(): boolean {
  const next: FormErrors = {}
  if (!selectedCustomer.value) {
    if (customerForm.name.trim().length < 3) next.name = 'Nome deve ter ao menos 3 caracteres.'
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customerForm.email.trim())) next.email = 'E-mail inválido.'
    if (customerForm.phone.trim() && (digitsOnly(customerForm.phone).length !== 13 || !normalizePhone(customerForm.phone))) next.phone = 'Telefone deve seguir o formato +00 (00) 00000-0000.'
    if (customerForm.cpfCnpj.trim() && !normalizeCpfCnpj(customerForm.cpfCnpj)) next.cpfCnpj = 'CPF/CNPJ inválido.'
    if (customerForm.cep.trim() && !normalizeCep(customerForm.cep)) next.cep = 'CEP deve ter 8 dígitos.'
  }
  if (!vehicleForm.brand.trim()) next.brand = 'Informe a marca.'
  if (!vehicleForm.model.trim()) next.model = 'Informe o modelo.'
  const year = Number(vehicleForm.year)
  if (!vehicleForm.year || year < 1886 || year > 2100) next.year = 'Ano inválido.'
  if (!vehicleForm.plate.trim()) next.plate = 'Informe a placa.'
  if (orderForm.expectedDelivery.trim() && !normalizeDateOnly(orderForm.expectedDelivery)) next.expectedDelivery = 'Informe uma data válida no formato dd/MM/yyyy.'
  errors.value = next
  return Object.keys(next).length === 0
}

function submit() {
  submitError.value = null
  if (!validate()) return
  mutation.mutate()
}

function updateCustomerPhone(value: string) { customerForm.phone = formatPhone(value) }
function updateCustomerCpfCnpj(value: string) { customerForm.cpfCnpj = formatCpfCnpj(value) }
function updateCustomerCep(value: string) { customerForm.cep = formatCep(value) }
function updateExpectedDelivery(value: string) { orderForm.expectedDelivery = formatDateInput(value) }
</script>

<template>
  <div class="nova-os-form">
    <CkAlert v-if="createdAccount" tone="success" class="success">
      Ordem de serviço criada. Conta de acesso ao Portal do Cliente criada para <strong>{{ createdAccount.name }}</strong>. Senha temporária — anote agora, ela não será exibida novamente:
      <div class="temp-password">
        <code class="ck-code">{{ createdAccount.temporaryPassword }}</code>
        <button type="button" class="copy" @click="copyPassword"><Copy :size="14" />{{ copied ? 'Copiada' : 'Copiar' }}</button>
      </div>
      O cliente deverá trocá-la no primeiro acesso ao Portal.
      <CkButton class="continue" @click="createdWorkOrderId && emit('created', createdWorkOrderId)">Ir para a OS</CkButton>
    </CkAlert>

    <CkAlert v-if="coverWarning" tone="info" class="cover-warning">A OS foi criada, mas a foto do veículo não pôde ser enviada. Você pode adicioná-la na tela da OS.</CkAlert>

    <form v-else @submit.prevent="submit">
      <CkAlert v-if="submitError" tone="error">{{ submitError }}</CkAlert>

      <section class="group">
        <h3>Cliente</h3>
        <div v-if="!selectedCustomer" class="customer-search">
          <div class="search-row">
            <CkField v-model="searchTerm" label="Buscar cliente existente" hint="Nome ou e-mail" :disabled="mutation.isPending.value" @keyup.enter.prevent="runSearch" />
            <CkButton type="button" variant="secondary" :disabled="mutation.isPending.value" @click="runSearch">Buscar</CkButton>
          </div>
          <ul v-if="searchResults.length" class="search-results">
            <li v-for="customer in searchResults" :key="customer.id">
              <button type="button" @click="selectCustomer(customer)">
                <strong>{{ customer.name }}</strong>
                <span>{{ customer.email }}</span>
              </button>
            </li>
          </ul>
          <p v-else-if="searched && activeSearch && !customersQuery.isFetching.value" class="empty-inline">
            Nenhum cliente encontrado para "{{ activeSearch }}". Preencha os dados abaixo para cadastrar um novo cliente.
          </p>
        </div>
        <div v-else class="selected-customer">
          <div><strong>{{ selectedCustomer.name }}</strong><span>{{ selectedCustomer.email }}</span></div>
          <CkButton type="button" variant="ghost" @click="clearSelection">Trocar cliente</CkButton>
        </div>
        <div v-if="!selectedCustomer" class="grid">
          <CkField v-model="customerForm.name" label="Nome completo" autocomplete="name" required :error="errors.name" :disabled="mutation.isPending.value" />
          <CkField v-model="customerForm.email" label="E-mail" type="email" autocomplete="email" required :error="errors.email" :disabled="mutation.isPending.value" />
          <CkField :model-value="customerForm.phone" label="Telefone (opcional)" type="tel" autocomplete="tel" placeholder="+00 (00) 00000-0000" inputmode="tel" :maxlength="19" hint="Ex.: +55 (34) 99999-9999" :error="errors.phone" :disabled="mutation.isPending.value" @update:model-value="updateCustomerPhone" />
          <CkField :model-value="customerForm.cpfCnpj" label="CPF/CNPJ (opcional)" placeholder="000.000.000-00 ou 00.000.000/0000-00" inputmode="numeric" :maxlength="18" hint="Informe apenas um documento" :error="errors.cpfCnpj" :disabled="mutation.isPending.value" @update:model-value="updateCustomerCpfCnpj" />
          <CkField :model-value="customerForm.cep" label="CEP (opcional)" placeholder="00000-000" inputmode="numeric" :maxlength="9" hint="Sem consulta automática" :error="errors.cep" :disabled="mutation.isPending.value" @update:model-value="updateCustomerCep" />
        </div>
      </section>

      <section class="group">
        <h3>Veículo</h3>
        <div class="grid">
          <CkField v-model="vehicleForm.brand" label="Marca" required :error="errors.brand" :disabled="mutation.isPending.value" />
          <CkField v-model="vehicleForm.model" label="Modelo" required :error="errors.model" :disabled="mutation.isPending.value" />
          <CkField v-model="vehicleForm.year" label="Ano" type="number" required :error="errors.year" :disabled="mutation.isPending.value" />
          <CkField v-model="vehicleForm.plate" label="Placa" required :error="errors.plate" :disabled="mutation.isPending.value" />
          <CkField v-model="vehicleForm.vin" label="VIN / chassi" hint="Opcional" :disabled="mutation.isPending.value" />
          <CkField v-model="vehicleForm.color" label="Cor" hint="Opcional" :disabled="mutation.isPending.value" />
        </div>
        <div class="cover-field">
          <span class="ck-field-label">Foto principal do veículo <span class="optional">(opcional)</span></span>
          <div class="cover-row">
            <button type="button" class="cover-pick" :class="{ 'has-image': coverPreview }" :disabled="mutation.isPending.value" :aria-label="coverPreview ? 'Trocar foto do veículo' : 'Enviar foto do veículo'" @click="triggerCoverPick">
              <img v-if="coverPreview" :src="coverPreview" alt="Prévia da foto do veículo">
              <span v-else class="cover-hint"><ImagePlus :size="20"/>Adicionar foto</span>
            </button>
            <button v-if="coverPreview" type="button" class="cover-remove" :disabled="mutation.isPending.value" @click="clearCover"><X :size="14"/>Remover</button>
          </div>
          <input ref="coverInput" type="file" accept="image/*" class="sr-only" @change="onCoverSelected">
        </div>
      </section>

      <section class="group">
        <h3>Ordem de serviço</h3>
        <div class="grid">
          <div class="ck-field">
            <label class="ck-field-label" for="technician">Técnico responsável</label>
            <select id="technician" v-model="orderForm.assignedTechnicianSubject" :disabled="mutation.isPending.value">
              <option value="">A definir</option>
              <option v-for="technician in technicians" :key="technician.keycloakSubject" :value="technician.keycloakSubject">{{ technician.displayName }}</option>
            </select>
          </div>
          <CkField :model-value="orderForm.expectedDelivery" label="Previsão de entrega" placeholder="dd/MM/yyyy" inputmode="numeric" :maxlength="10" hint="Opcional" :disabled="mutation.isPending.value" @update:model-value="updateExpectedDelivery" />
        </div>
      </section>

      <CkButton type="submit" class="submit" :disabled="mutation.isPending.value">{{ mutation.isPending.value ? 'Criando…' : 'Criar ordem de serviço' }}</CkButton>
    </form>
  </div>
</template>

<style scoped>
.group { margin-bottom: 22px; }
.group h3 { margin: 0 0 12px; font-size: 15px; color: var(--color-text); }
.grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px 16px; }
.ck-field-label { display: block; margin-bottom: var(--space-1); font-size: var(--fs-sm); font-weight: var(--fw-semibold); color: var(--color-text-muted); }
select { width: 100%; min-height: 44px; padding: 10px 14px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); font: inherit; }
select:disabled { cursor: not-allowed; opacity: .6; }
.customer-search { margin-bottom: 16px; }
.search-row { display: flex; align-items: flex-start; gap: 12px; }
.search-row > :first-child { flex: 1; }
.search-row > :deep(.ck-button) { flex: 0 0 auto; margin-top: 25px; }
.search-results { display: grid; gap: 4px; margin: 10px 0 0; padding: 0; list-style: none; }
.search-results button { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; width: 100%; padding: 10px 14px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); text-align: left; cursor: pointer; }
.search-results button:hover, .search-results button:focus-visible { border-color: var(--color-accent); }
.search-results span { color: var(--color-text-muted); font-size: 13px; }
.empty-inline { margin: 10px 0 0; color: var(--color-text-muted); font-size: 13px; }
.selected-customer { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; padding: 12px 16px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); }
.selected-customer span { display: block; color: var(--color-text-muted); font-size: 13px; }
.temp-password { display: flex; align-items: center; gap: 10px; margin: 10px 0; padding: 10px 14px; border-radius: var(--radius-md); background: rgba(120, 100, 80, .12); }
.temp-password code { font-size: 15px; letter-spacing: .04em; }
.copy { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); padding: 4px 10px; background: transparent; color: inherit; cursor: pointer; font-size: 12px; }
.copy:hover { border-color: var(--color-accent); color: var(--color-accent); }
.continue { margin-top: 12px; }
.submit { width: 100%; }
.cover-field { margin-top: 14px; }
.optional { font-weight: 400; color: var(--color-text-faint); }
.cover-row { display: flex; align-items: center; gap: 12px; margin-top: 6px; }
.cover-pick { position: relative; display: flex; align-items: center; justify-content: center; width: 160px; height: 100px; flex-shrink: 0; padding: 0; border: 1px dashed var(--color-border); border-radius: var(--radius-md); overflow: hidden; cursor: pointer; background: var(--color-surface-muted); color: var(--color-text-muted); }
.cover-pick.has-image { border-style: solid; }
.cover-pick:disabled { cursor: not-allowed; opacity: .6; }
.cover-pick img { width: 100%; height: 100%; object-fit: cover; }
.cover-hint { display: inline-flex; flex-direction: column; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; }
.cover-remove { display: inline-flex; align-items: center; gap: 5px; padding: 6px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: transparent; color: var(--color-text-muted); font-size: 12px; cursor: pointer; }
.cover-remove:hover { border-color: var(--color-accent); color: var(--color-accent); }
.cover-warning { margin-bottom: 16px; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }

@media (max-width: 560px) {
  .grid { grid-template-columns: 1fr; }
  .search-row { flex-direction: column; align-items: stretch; }
  .search-row > :deep(.ck-button) { width: 100%; margin-top: 0; }
}
</style>
